import { useCallback } from 'react';
import type { MapLedgerEventView, MapPhoneCallView } from '@mark43/cad-api';
import { useEsriMapStore } from './use-esri-map-store';
import { usePhoneCallsStore } from '~/phone-calls/use-phone-calls';
import type { PhoneCallView } from '~/phone-calls/types';

export function getMapPhoneCallPhoneNumberId(phoneCall: MapPhoneCallView): number | undefined {
    return phoneCall.phoneNumberId ?? phoneCall.id;
}

export function getPhoneNumberIdsForEvent(
    mapLedgerEvent: MapLedgerEventView | undefined,
    activePhoneCallsList: PhoneCallView[],
    eventId?: number
): number[] {
    if (!eventId) return [];

    const phoneNumberIds =
        mapLedgerEvent?.phoneCalls
            ?.map(getMapPhoneCallPhoneNumberId)
            .filter((phoneNumberId): phoneNumberId is number => !!phoneNumberId) ?? [];

    // Prefer map ledger as the canonical source. Fall back to activePhoneCallsList
    // for cases where a phone call arrived via real-time stream but the map ledger
    // hasn't been updated yet.
    if (phoneNumberIds.length) {
        return phoneNumberIds;
    }

    return activePhoneCallsList
        .filter((phoneCall) => phoneCall.matchedActiveEventId === eventId)
        .map((phoneCall) => phoneCall.phoneNumberId)
        .filter((phoneNumberId): phoneNumberId is number => !!phoneNumberId);
}

export function usePhoneNumberIdsForEvent() {
    const mapStore = useEsriMapStore();
    const phoneCallsStore = usePhoneCallsStore();

    return useCallback(
        (eventId?: number) =>
            getPhoneNumberIdsForEvent(
                eventId ? mapStore.getMapLedgerEvent(eventId) : undefined,
                phoneCallsStore.activePhoneCallsList,
                eventId
            ),
        [mapStore, phoneCallsStore.activePhoneCallsList]
    );
}
